/**
 * Options (docs/spec/04 §6): every setting, grouped, left/right to change, applied at once, one
 * line explaining the selected row. Also opened from the pause menu; BACK returns there.
 */

import type { Options } from '../../platform/options';
import type { Screen } from '../../render/screen';
import { UiScene } from '../kit/base';
import { drawRule } from '../kit/chrome';
import { wrapIndex } from '../kit/menuModel';
import type { RowColumns } from '../kit/optionRow';
import { drawOptionRow, rowPress } from '../kit/optionRow';
import { ACCENT, CX, FAINT, HEADING, LEFT, RIGHT } from '../kit/theme';
import type { MenuAction, PointerInput } from '../scene';
import type { OptionRow } from './optionsModel';
import { OPTION_ROWS, optionLines, rowAtY, valueText } from './optionsModel';

const TOP = 28;
const PITCH = 11;
const GROUP_GAP = 3;
const COLS: RowColumns = { label: 16, right: RIGHT - 8 };
const HELP_RULE_Y = 252;
const HELP_Y = 258;
const LINES = optionLines(TOP, PITCH, GROUP_GAP);

/** Remembered while the page is open, so returning from Controls lands where you were. */
let lastRow = 0;

export class OptionsScene extends UiScene {
  protected readonly heading = 'OPTIONS';
  /** Options can sit over a paused game: leave its music alone. */
  protected override readonly track = null;
  private row = lastRow;

  private get current(): OptionRow | undefined {
    return OPTION_ROWS[this.row];
  }

  protected override describe(): string {
    return `Options. ${this.rowText()}`;
  }

  private rowText(): string {
    const r = this.current;
    return r ? `${r.label}: ${valueText(r.value(this.services.options.get()))}. ${r.help}` : '';
  }

  protected override hint(): string {
    return `◄► CHANGE ▲▼ MOVE ${this.words().back} BACK`;
  }

  protected override touchHint(): string {
    return 'TAP A ROW TO CHANGE IT';
  }

  protected paint(screen: Screen): void {
    const o = this.services.options.get();
    for (const line of LINES) {
      if (line.kind === 'group') {
        screen.text(line.label, LEFT, line.y, HEADING);
        const x = LEFT + line.label.length * 8 + 6;
        screen.fill(x, line.y + 3, RIGHT - x, 1, FAINT);
        continue;
      }
      const r = OPTION_ROWS[line.row];
      if (r) drawOptionRow(screen, line.y, r.label, r.value(o), line.row === this.row, this.ms, COLS);
    }
    drawRule(screen, HELP_RULE_Y, this.ms, FAINT);
    screen.text(this.current?.help ?? '', CX, HELP_Y, ACCENT, 'center');
  }

  private select(row: number): void {
    const next = wrapIndex(row, OPTION_ROWS.length);
    if (next === this.row) return;
    this.row = next;
    lastRow = next;
    this.sound('move');
    this.say(this.rowText());
  }

  /** Save a changed setting; the same settings back means the row was at its end. */
  private change(next: Options): void {
    if (JSON.stringify(next) === JSON.stringify(this.services.options.get())) return this.sound('error');
    this.services.options.update(next);
    this.sound('move');
    this.say(this.rowText());
  }

  private open(): void {
    this.sound('select');
    this.host.push({ to: 'controls' });
  }

  protected onAction(a: MenuAction): void {
    const r = this.current;
    if (a === 'up') this.select(this.row - 1);
    else if (a === 'down') this.select(this.row + 1);
    else if (!r) return;
    else if (a === 'left' || a === 'right') {
      if (!r.step) return a === 'right' ? this.open() : this.sound('error');
      this.change(r.step(this.services.options.get(), a === 'left' ? -1 : 1));
    } else if (a === 'confirm') {
      if (!r.step) this.open();
      else this.change(r.step(this.services.options.get(), 1));
    }
  }

  protected override onPointer(p: PointerInput): void {
    if (p.kind === 'wheel') return this.select(this.row + Math.sign(p.delta));
    const row = rowAtY(LINES, PITCH, p.y);
    if (row < 0) return;
    if (p.kind === 'move') return this.select(row);
    if (p.kind !== 'down' || p.button !== 0) return;
    this.select(row);
    const r = this.current;
    if (!r) return;
    if (!r.step) return this.open();
    const o = this.services.options.get();
    const press = rowPress(r.value(o), COLS, p.x);
    if ('set' in press) this.change(r.set ? r.set(o, press.set) : r.step(o, 1));
    else this.change(r.step(o, press.step));
  }
}

/** Tests: forget the remembered row. */
export function resetOptionsMemory(): void {
  lastRow = 0;
}
